import { useContext, useEffect, useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  CheckCircleIcon,
  WarningCircleIcon,
  SpinnerIcon,
  CaretDownIcon,
  CaretRightIcon,
  DesktopIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { AuthContext } from '@/context/auth.context';
import { orpc } from '@/api/rpc/client';
import { AgentSessionViewer } from '@/components/common/AgentSessionViewer';
import { usePortalActionRunStatus } from '@/api/rpc/hooks/accounts-receivable/useArCollectionsAgentActions';
import { cn } from '@/lib/utils';

interface PortalActionRunStatusProps {
  workflowRunId: string;
  /** Human label of the action ("Pull backup from portal"), when known. */
  label?: string;
}

const TERMINAL_OK = 'completed';

/**
 * Live status card for a portal action triggered from the Nami chat. Polls the
 * run, shows progress, embeds the operator's live browser view while it runs,
 * and refreshes the thread's evidence once it completes so the pulled documents
 * appear without a page reload.
 */
export function PortalActionRunStatus({
  workflowRunId,
  label,
}: PortalActionRunStatusProps) {
  const t = useGT();
  const { currentUser } = useContext(AuthContext);
  const queryClient = useQueryClient();

  const {
    status,
    stopReason,
    completedSteps,
    totalSteps,
    operator,
    isTerminal,
  } = usePortalActionRunStatus(workflowRunId);

  const [liveOpen, setLiveOpen] = useState(true);

  // When the run completes, refresh the thread's evidence + detail so the
  // pulled documents (and any refreshed recommendation) appear immediately.
  useEffect(() => {
    if (!isTerminal || status !== TERMINAL_OK) return;
    void queryClient.invalidateQueries({
      queryKey: orpc.accountsReceivable.arCollectionThreads.listEvidence.key(),
    });
    void queryClient.invalidateQueries({
      queryKey: orpc.accountsReceivable.arCollectionThreads.get.key(),
    });
  }, [isTerminal, status, queryClient]);

  const isRunning = !isTerminal;
  const failed = isTerminal && status !== TERMINAL_OK;
  const sessionId = operator?.sessionId ?? null;
  const showLiveView = isRunning && Boolean(sessionId) && currentUser;

  return (
    <div className="mt-2 space-y-2 rounded-md border p-3 text-xs">
      <div className="flex items-center gap-2">
        {isRunning ? (
          <SpinnerIcon className="size-4 animate-spin text-muted-foreground" />
        ) : failed ? (
          <WarningCircleIcon className="size-4 text-red-600" />
        ) : (
          <CheckCircleIcon className="size-4 text-emerald-600" />
        )}
        <span className="font-medium">
          {label ?? t('Portal action')}
          {' — '}
          {isRunning ? (
            <T>running</T>
          ) : failed ? (
            <T>failed</T>
          ) : (
            <T>filed onto this thread</T>
          )}
        </span>
        {totalSteps > 0 && (
          <span className="text-muted-foreground ml-auto tabular-nums">
            {completedSteps}/{totalSteps}
          </span>
        )}
      </div>

      {failed && stopReason && (
        <p className="text-muted-foreground">{stopReason}</p>
      )}

      {showLiveView && (
        <div className="space-y-1">
          <button
            type="button"
            onClick={() => setLiveOpen((v) => !v)}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            {liveOpen ? (
              <CaretDownIcon className="size-3" />
            ) : (
              <CaretRightIcon className="size-3" />
            )}
            <DesktopIcon className="size-3.5" />
            <T>Watch the browser agent</T>
          </button>
          <div className={cn(liveOpen ? 'block' : 'hidden')}>
            <AgentSessionViewer
              runId={workflowRunId}
              organizationId={currentUser.organizationId}
              sessionId={sessionId}
            />
          </div>
        </div>
      )}
    </div>
  );
}
